import React, { useMemo } from 'react';
import { AlertTriangle, CircleDashed, ShieldCheck, Database } from 'lucide-react';
import { Card } from '../../../../components/ui/Card';
import { runSsotChecks, getGuardedSourceCount, type SsotCheck } from './ssotRegistry';

/**
 * SsotStatus — de MELDINGEN over single-sources-of-truth.
 *
 * WAAROM DIT NIET DE BRONNEN OPSOMT
 *
 * De catalogus hoort op één plek: het SSOT Registry-tabblad. Dit scherm somde tot
 * 12 aug 2026 zes bronnen op mét hun waarden, en dat was een tweede overzicht van
 * iets waarvan er per definitie één hoort te zijn — precies de fout die dit werk
 * moet uitbannen.
 *
 * Wat een beheerder hier komt halen is de andere vraag: **is er iets gedrift, en
 * wat moet er nog gebeuren.** Een bewaking die slaagt is dus geen regel maar een
 * getal; alleen wat niet klopt krijgt ruimte. Zo is de lengte van dit scherm
 * evenredig aan het werk dat er ligt, en niet aan het aantal bronnen.
 *
 * WAAROM DE ONDERSTE LIJST NOOIT GROEN KAN
 *
 * Die items zijn niet in de browser te meten. Ze staan in een aparte sectie en
 * kunnen nooit "in orde" tonen: een scherm dat onbewezen items groen laat, is de
 * volgende generatie van het probleem dat het moest oplossen.
 */
function FindingRow({
  check
}: {
  check: SsotCheck;
}) {
  const drift = check.status === 'drift';
  return <li className={`rounded-xl border p-4 ${drift ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/40'}`} data-id="element-8366" data-mp="3pleaz5yovu7w">
      <div className="flex items-start gap-3" data-id="element-8367" data-mp="3bl3la6948lob">
        <span className="mt-0.5 flex-shrink-0" data-id="element-8368" data-mp="3glhonazrv7g0">
          {drift ? <AlertTriangle className="h-5 w-5 text-red-600" aria-hidden="true" data-id="element-8369" data-mp="3gn2nvdx17ek7" /> : <CircleDashed className="h-5 w-5 text-amber-600" aria-hidden="true" data-id="element-8370" data-mp="31pjxk74pgtan" />}
        </span>
        <div className="min-w-0 flex-1" data-id="element-8371" data-mp="36omsgyqz1j4w">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1" data-id="element-8372" data-mp="36po9djuo4zuj">
            <h4 className="font-bold text-slate-900" data-id="element-8373" data-mp="zxnca5sqk52o">{check.concern}</h4>
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-8374" data-mp="14wgpry80vw2u">
              {drift ? 'Gedrift' : 'Nog open'}
            </span>
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-700" data-id="element-8375" data-mp="yg89l9vesdhh">
            {check.detail}
          </p>
          <p className="mt-2 break-all font-mono text-xs text-slate-500" data-id="element-8376" data-mp="tgc4t8ew0i0v">
            {check.source}
          </p>
        </div>
      </div>
    </li>;
}
export function SsotStatus() {
  const checks = useMemo(() => runSsotChecks(), []);
  const guardedCount = useMemo(() => getGuardedSourceCount(), []);
  const drifting = checks.filter(c => c.status === 'drift');
  const open = checks.filter(c => c.status === 'open');
  const passing = guardedCount - drifting.length;
  return <div className="space-y-6" data-id="element-8377" data-mp="18e4hztwyoubw">
      {drifting.length === 0 ? <Card className="border-emerald-200 bg-emerald-50/50 p-6" data-id="element-8378" data-mp="18dcu759lw0w6">
          <div className="flex items-start gap-4" data-id="element-8379" data-mp="24wk7d9ubjtcj">
            <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600" data-id="element-8380" data-mp="24y5518bxjsem">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" data-id="element-8381" data-mp="3islllnoqzsyk" />
            </div>
            <div data-id="element-8382" data-mp="3nr56rlvip0pv">
              <h3 className="text-lg font-bold text-emerald-900" data-id="element-8383" data-mp="3nqn7shhh08e9">
                Geen gedrifte bronnen
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-emerald-800" data-id="element-8384" data-mp="3rryykqc2yzo1">
                <span className="font-semibold tabular-nums" data-id="element-8385" data-mp="mpf7hildjne">{passing}</span> van{' '}
                <span className="font-semibold tabular-nums" data-id="element-8386" data-mp="5ls6lz5nfrcw">{guardedCount}</span>{' '}
                bewaakte bronnen zijn zojuist naast hun consument gelegd en staan
                gelijk. Dit is gemeten, niet aangenomen — de vergelijking draait
                bij elke keer dat dit scherm opent.
              </p>
            </div>
          </div>
        </Card> : <Card className="border-red-200 bg-red-50/60 p-6" data-id="element-8387" data-mp="jc5ey2mgd82r">
          <div className="flex items-start gap-4" data-id="element-8388" data-mp="oct92x5za517">
            <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600" data-id="element-8389" data-mp="oa6ysprpdbfa">
              <AlertTriangle className="h-5 w-5" aria-hidden="true" data-id="element-8390" data-mp="fnvxkni8ohnt" />
            </div>
            <div data-id="element-8391" data-mp="apkoiezrqb6n">
              <h3 className="text-lg font-bold text-red-900" data-id="element-8392" data-mp="aq2va5k247st">
                {drifting.length}{' '}
                {drifting.length === 1 ? 'bron is' : 'bronnen zijn'} gedrift
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-red-800" data-id="element-8393" data-mp="keuhdx2frxvt">
                Er bestaat een tweede definitie van iets dat één keer hoort te
                bestaan. Dit levert geen foutmelding op — het levert twee
                antwoorden op dezelfde vraag.
              </p>
            </div>
          </div>
        </Card>}

      {drifting.length > 0 && <section aria-labelledby="ssot-drift-heading" data-id="element-8394" data-mp="6lcxuc9r2l5s">
          <div className="mb-3 flex items-center gap-2" data-id="element-8395" data-mp="1nl55g1a6v0j">
            <AlertTriangle className="h-4 w-4 text-red-600" aria-hidden="true" data-id="element-8396" data-mp="1i2ut0nd9mcgp" />
            <h3 id="ssot-drift-heading" className="text-sm font-bold uppercase tracking-wide text-slate-600" data-id="element-8397" data-mp="1i4z4prheofrj">
              Nu gedrift
            </h3>
          </div>
          <ul className="space-y-3" data-id="element-8398" data-mp="1kmj6kf1weix9">
            {drifting.map(c => <FindingRow key={c.id} check={c} data-id="element-8399" data-mp="1pm454od5op9g" />)}
          </ul>
        </section>}

      <section aria-labelledby="ssot-open-heading" data-id="element-8400" data-mp="1pk15dcm7bhrj">
        <div className="mb-3 flex items-center gap-2" data-id="element-8401" data-mp="1bnnvaojzyin3">
          <CircleDashed className="h-4 w-4 text-slate-500" aria-hidden="true" data-id="element-8402" data-mp="1fo7wq4bls8f1" />
          <h3 id="ssot-open-heading" className="text-sm font-bold uppercase tracking-wide text-slate-600" data-id="element-8403" data-mp="1fqak0gldfng2">
            Nog te doen ({open.length})
          </h3>
        </div>
        <p className="mb-3 text-sm leading-relaxed text-slate-600" data-id="element-8404" data-mp="28cdganqx7rda">
          Vastgelegde schuld die de browser niet kan controleren. Deze items tonen
          nooit groen: ze zijn open tot iemand ze opheft en de regel hier weghaalt.
        </p>
        <ul className="space-y-3" data-id="element-8405" data-mp="2mcwl7m03ahb8">
          {open.map(c => <FindingRow key={c.id} check={c} data-id="element-8406" data-mp="2mb3e65lkdv2p" />)}
        </ul>
      </section>

      {/* De catalogus staat één klik verderop; hier niet herhalen. */}
      <Card className="p-4" data-id="element-8407" data-mp="30z3ebydyszvz">
        <div className="flex items-start gap-3" data-id="element-8408" data-mp="30ybsvge30ijf">
          <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white" data-id="element-8409" data-mp="30x0rfn83zmvh">
            <Database className="h-4 w-4" aria-hidden="true" data-id="element-8410" data-mp="2ry5jo6pf24q2" />
          </span>
          <p className="text-sm leading-relaxed text-slate-700" data-id="element-8411" data-mp="2mzkj26695j13">
            <span className="font-bold" data-id="element-8412" data-mp="2mz2gkrszy8kq">Zoek je de bronnen zelf?</span> Welke
            single-sources-of-truth er zijn, wat ze nú zeggen en wie eruit leest,
            staat volledig onder{' '}
            <span className="font-semibold" data-id="element-8413" data-mp="2d87srj03r0x6">Inhoud → SSOT Registry</span>. Dit
            scherm gaat alleen over wat er nog niet klopt.
          </p>
        </div>
      </Card>
    </div>;
}